import React, { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Path } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { slugify } from 'utils/helpers';
import { useKnowledgeBase } from './knowledgeBaseContext';

type UseKnowledgeBaseLinkReturn = {
	key: string;
	to: Partial<Path>;
	label: string;
};

type UseKnowledgeBaseLinkOptions = {
	label: string;
};

export const useKnowledgeBaseArticleLink = (label: string) => {
	const { context, queries } = useKnowledgeBase();
	const query = useQuery(
		['KNOWLEDGE_BASE_ARTICLE_LIST_LABEL', { context, labels: [label] }],
		() => queries.getArticlesByLabelsOrIds([label]),
		{ enabled: true, initialData: null, select: (data) => data?.[0] || null }
	);

	const { data } = query;
	return {
		query,
		link: data
			? {
					key: `kba-${data.id}`,
					to: {
						pathname: `knowledge-base/article/${data.id}${data.slug ? `-${slugify(data.slug)}` : ''}`,
					},
					label: data.title,
				}
			: null,
	};
};

type KnowledgeBaseLinkProps = UseKnowledgeBaseLinkOptions &
	PropsWithChildren<{
		render: (options: PropsWithChildren<UseKnowledgeBaseLinkReturn>) => ReactNode;
	}>;

export const KnowledgeBaseArticleLink = ({ label, render, children }: KnowledgeBaseLinkProps) => {
	const { t } = useTranslation();
	const {
		query: { isError, isLoading, isSuccess },
		link,
	} = useKnowledgeBaseArticleLink(label);

	if (isError) {
		return <>{render({ key: 'error', to: { hash: '#error' }, label: t('knowledgeBase.dynamicLink.error') })}</>;
	}

	if (isLoading) {
		return (
			<>{render({ key: 'loading', to: { hash: '#loading' }, label: t('knowledgeBase.dynamicLink.loading') })}</>
		);
	}

	if (isSuccess && link) {
		return (
			<>
				{render({
					...link,
					children,
				})}
			</>
		);
	}

	return null;
};

export const useKnowledgeBaseDirectoryLink = (label: string) => {
	const { context, queries } = useKnowledgeBase();
	const query = useQuery(
		['KNOWLEDGE_BASE_DIRECTORY_LIST_LABEL', { context, labels: [label] }],
		() => queries.getDirectoryByLabels([label]),
		{ enabled: true, initialData: null, select: (data) => data?.[label] }
	);

	const { data } = query;
	return {
		query,
		link: data
			? {
					key: `kba-${data.id}`,
					to: {
						pathname: `knowledge-base/directory/${data.id}${data.slug ? `-${slugify(data.slug)}` : ''}`,
					},
					label: data.name,
				}
			: null,
	};
};

export const KnowledgeBaseDirectoryLink = ({ label, render, children }: KnowledgeBaseLinkProps) => {
	const { t } = useTranslation();
	const {
		query: { isError, isLoading, isSuccess },
		link,
	} = useKnowledgeBaseDirectoryLink(label);

	if (isError) {
		return <>{render({ key: 'error', to: { hash: '#error' }, label: t('knowledgeBase.dynamicLink.error') })}</>;
	}

	if (isLoading) {
		return (
			<>{render({ key: 'loading', to: { hash: '#loading' }, label: t('knowledgeBase.dynamicLink.loading') })}</>
		);
	}

	if (isSuccess && link) {
		return (
			<>
				{render({
					...link,
					children,
				})}
			</>
		);
	}

	return null;
};
